import React, {memo} from 'react';
import NextLink from 'next/link';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {fadeInUp} from '@/animation/scroll-animation';
import {AnimatedComponent} from '@/components/Ui-components/AnimatedComponent';
import DisplayImage from '@/components/Ui-components/display-image';
import {cdnUrl} from '@/util/cdnImage';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import {borderRadius, colors} from '@/styles/tokens';

// ─── Module-level sx constants ────────────────────────────────────────────────
const portfolioCardSx = {
    position: 'relative',
    height: {lg: 600, md: 520, sm: 440, xs: 400},
    borderRadius: borderRadius['3xl'],
    overflow: 'hidden'
} as const;

const overlayBoxSx = {position: 'absolute', inset: 0, zIndex: 2} as const;
const portfolioContentBoxSx = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 3,
    background: colors.background.default,
    px: {lg: 3, md: 2, sm: 2, xs: 2},
    py: {lg: 2, md: 1, sm: 1, xs: 1}
} as const;
const buttonNoTransformSx = {textTransform: 'none'} as const;

// The heading doubles as the card's crawlable link: descriptive anchor text
// pointing at the case study, styled to look exactly like the plain heading it
// replaced. globals.scss carries a base `a { color; text-decoration; font-weight: 400 }`
// rule that would otherwise repaint and thin the heading, so each of those three
// is reset to inherit here rather than left to cascade.
const headingLinkSx = {
    color: 'inherit',
    fontWeight: 'inherit',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    lineHeight: 'inherit',
    textDecoration: 'none',
    '&:hover': {textDecoration: 'underline'}
} as const;

// Original also declared `height: auto 0` — invalid CSS (a length can't be
// "auto 0"), so it computed to nothing and isn't carried over here.
// !important preserved on the breakpoint overrides: DisplayImage can set an
// inline `style` width/height for static image imports, which only an
// `!important` external rule can beat.
const responsiveLogoSx = {
    width: 'auto',
    '@media (max-width: 768px)': {
        width: '110px !important',
        height: '50px !important'
    },
    '@media (max-width: 600px)': {
        width: '90px !important',
        height: '50px !important'
    }
} as const;

// ─────────────────────────────────────────────────────────────────────────────

interface PortfolioCardProps {
    item: any;
    index: number;
    openForm: () => void;
}

// A few entries in the data file omit the leading slash, which would resolve
// relative to the current page (/blockchain-development-services/case-study/...)
// and 404. Normalising here keeps every card pointing at the site root.
const toHref = (redirect?: string) => {
    if (!redirect) return undefined;
    if (/^https?:\/\//.test(redirect)) return redirect;
    return `/${redirect.replace(/^\//, '')}`;
};

const PortfolioCard = ({item, index, openForm}: PortfolioCardProps) => {
    const isLCP = index === 0;
    const href = toHref(item.redirect);

    return (
        <Grid item lg={6} md={6} sm={6} xs={12} key={item.id ?? index}>
            <AnimatedComponent className="portfolio-card-main" variants={isLCP ? undefined : fadeInUp} delay={isLCP ? 0 : index * 0.2}>
                <Box className="portfolio-top-card" sx={portfolioCardSx}>
                    <DisplayImage
                        className="zoom-image"
                        src={item.imgSrc}
                        alt={item.heading}
                        priority={isLCP}
                        fetchPriority={isLCP ? 'high' : 'auto'}
                        sizes="(max-width: 600px) 50vw, (max-width: 1200px) 50vw, 33vw"
                        placeholder="blur"
                        blurDataURL={cdnUrl('blur-placeholder.webp')}
                        style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'cover',
                            transition: 'transform 0.4s ease'
                        }}
                    />
                    <Box sx={overlayBoxSx} />
                    <Box sx={portfolioContentBoxSx}>
                        <Typography
                            variant="h3"
                            fontSize={{xs: 18, sm: 22, md: 24, lg: 32}}
                            className="font-headers"
                            lineHeight={{xs: '1.8rem', md: '2.5rem', lg: '2.8rem'}}
                        >
                            {href ? (
                                <Box component={NextLink} href={href} target="_blank" rel="noopener" sx={headingLinkSx}>
                                    {item.heading}
                                </Box>
                            ) : (
                                item.heading
                            )}
                        </Typography>

                        <Box display="flex" gap={{xs: 1, sm: 3, md: 4}} mb={2} alignItems="center">
                            <Box>
                                <Typography
                                    className="font-headers"
                                    fontSize={{xs: 16, sm: 18, md: 22, lg: 34}}
                                    color={colors.primary.deep}
                                >
                                    {item.keyPoint?.[0]?.header}
                                </Typography>
                                <Typography fontSize={{xs: 12, sm: 13, lg: 16}}>{item.keyPoint?.[0]?.body}</Typography>
                            </Box>
                            <Box height={40} borderLeft={`1px solid ${colors.neutral.grayLight}`} />
                            <Box>
                                <Typography
                                    className="font-headers"
                                    fontSize={{xs: 16, sm: 18, md: 22, lg: 34}}
                                    color={colors.primary.deep}
                                >
                                    {item.keyPoint?.[1]?.header}
                                </Typography>
                                <Typography fontSize={{xs: 12, sm: 13, lg: 16}}>{item.keyPoint?.[1]?.body}</Typography>
                            </Box>
                        </Box>

                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            {item.buttonText && (
                                <MUIButton
                                    {...(href ? {component: NextLink, href, target: '_blank', rel: 'noopener'} : {onClick: openForm})}
                                    aria-label={`${item.buttonText}: ${item.heading}`}
                                    label={item.buttonText}
                                    variant="contained"
                                    size="medium"
                                    className="button-bg-primary animated-button moving-icon-button latest-portfolio-button"
                                    sx={buttonNoTransformSx}
                                />
                            )}
                            <Box>
                                {item.logoSrc && (
                                    <Box
                                        component={DisplayImage}
                                        src={item.logoSrc}
                                        alt="Logo"
                                        style={{objectFit: 'contain'}}
                                        sx={responsiveLogoSx}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </AnimatedComponent>
        </Grid>
    );
};

export default memo(PortfolioCard);
